import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	Button,
	Dimmer,
	Form,
	Grid,
	GridColumn,
	Header,
	Label,
	Loader,
	Portal,
	Segment, SegmentGroup, Sidebar
} from 'semantic-ui-react';

import { saveEval } from '../../../api/nora/eval';

import useForm from '../../../hooks/useForm';

// import { useAuthState } from '../../../contexts/AuthContext';
import { useSubmissionState } from '../../../contexts/SubmissionsContext';

import FormSelect from '../../Form/FormSelect';
import FormTextArea from '../../Form/FormTextArea';

const EvaluationModal = () => {
	// const { user } = useAuthState();
	const {
		document,
		evalError,
		evaluation
	} = useSubmissionState();

	const styles = useMemo( () => ( {
		header: { margin: 0 },
		sidebar: { zIndex: 1000 },
		sidebarHeading: {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center'
		},
		stepInfoContainer: {
			display: 'flex',
			alignItems: 'flex-start'
		},
		stepInfo: {
			flexGrow: 1,
			marginLeft: '1rem'
		},
		stepInfoHeader: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			flexGrow: 1,
			height: '24px',
			marginBottom: '1rem'
		},
		stepInfoTitle: { margin: 0 },
		stepControlsContainer: { padding: '2rem' }
	} ), [] );

	const {
		formData,
		hasChanged,
		setFormData,
		updateField
	} = useForm();

	const [ step/* , setStep */ ] = useState( 0 );
	const [ sidebar, setSidebar ] = useState( true );
	const [ saving, setSaving ] = useState( false );

	const handleMinimizeSidebar = useCallback( () => {
		setSidebar( false );
	}, [] );

	const handleMaximizeSidebar = useCallback( () => {
		setSidebar( true );
	}, [] );

	const stepData = useMemo(
		() => evaluation?.steps[ step ],
		[
			evaluation,
			step
		]
	);

	const attributeTypes = useMemo(
		() => stepData?.attributes.reduce(
			(
				acc,
				{
					id,
					type
				}
			) => ( {
				...acc,
				[ id ]: type.id
			} ),
			{}
		),
		[ stepData?.attributes ]
	);

	// const userEval = useMemo(
	// 	() => stepData?.evaluations.find( current => current.user.id === user.id ),
	// 	[
	// 		stepData?.evaluations,
	// 		user.id
	// 	]
	// );

	const currentEval = useMemo( () => stepData?.evaluations[ 0 ], [ stepData?.evaluations ] );

	// set form data to existing currentEval for the current step and user
	useEffect( () => {
		if ( currentEval ) {
			let data = currentEval.data.reduce(
				( acc, current ) => {
					switch ( attributeTypes[ current.attributeID ] ) {
						case 6: // select
						case 10: // radio
							return {
								...acc,
								[ current.attributeID ]: current.optionID
							};

						default:
							return {
								...acc,
								[ current.attributeID ]: current.value
							};
					}
				},
				{}
			);

			setFormData( data );
		}
	}, [
		attributeTypes,
		currentEval,
		setFormData
	] );

	const handleSaveEvaluation = useCallback( async () => {
		try {
			if ( currentEval ) {
				setSaving( true );

				let data = {
					...currentEval,
					data: currentEval.data.map( current => {
						switch ( attributeTypes[ current.attributeID ] ) {
							case 6: // select
							case 10: // radio
								return {
									...current,
									optionID: formData[ current.attributeID ]
								};

							default:
								return {
									...current,
									value: formData[ current.attributeID ]
								};
						}
					} )
				};

				await saveEval( document?.id, data );
			}
		} catch ( error ) {
			// handle error
		}

		setSaving( false );
	}, [
		attributeTypes,
		document?.id,
		currentEval,
		formData
	] );

	const renderStepControls = useCallback( () => stepData?.attributes.map( ( {
		id,
		isRequired,
		label,
		options,
		type
	} ) => {
		switch ( type.id ) {
			case 1: {
				return <FormTextArea
					key={ id }
					fieldLabel={ label }
					name={ `${ id }` }
					onChange={ updateField }
					required={ isRequired }
					value={ formData[ id ] }
				/>;
			}

			case 6: {
				const fieldOptions = options.map( ( { id, label } ) => ( { key: id, value: id, text: label } ) );

				return <FormSelect
					key={ id }
					fieldLabel={ label }
					name={ `${ id }` }
					onChange={ updateField }
					options={ fieldOptions }
					required={ isRequired }
					value={ formData[ id ] }
				/>;
			}

			default:
				return null;
		}
	} ), [
		formData,
		stepData?.attributes,
		updateField
	] );

	return (
		<Portal
			closeOnDocumentClick={ false }
			open
		>
			<Sidebar
				as={ SegmentGroup }
				direction="bottom"
				style={ styles.sidebar }
				visible={ !sidebar }
			>
				{ evaluation
					? <Segment
						secondary
						style={ styles.sidebarHeading }
					>
						<Header style={ styles.header }>Evaluation: { evaluation.name } - { evaluation.agency.name }</Header>
						<Button
							icon="window maximize outline"
							onClick={ handleMaximizeSidebar }
						/>
					</Segment>
					: null
				}
			</Sidebar>

			<Sidebar
				as={ SegmentGroup }
				direction="bottom"
				style={ styles.sidebar }
				visible={ sidebar }
			>
				{ evaluation
					? <>
						<Segment
							secondary
							style={ styles.sidebarHeading }
						>
							<Header style={ styles.header }>Evaluation: { evaluation.name } - { evaluation.agency.name }</Header>
							<Button
								icon="window minimize outline"
								onClick={ handleMinimizeSidebar }
							/>
						</Segment>
						<Segment style={ styles.content }>
							<Grid
								columns="equal"
								divided
							>
								<GridColumn style={ styles.stepInfoContainer }>
									<div>
										<Label
											circular
											color="pink"
										>
											{ stepData.ordinal }
										</Label>
									</div>
									<div style={ styles.stepInfo }>
										<div style={ styles.stepInfoHeader }>
											<Header style={ styles.stepInfoTitle }>{ stepData.title }</Header>
											{ stepData.points && stepData.percentage
												? <p>Weight: { stepData.percentage }% / { stepData.points } Pts.</p>
												: null
											}
										</div>
										<p>{ stepData.instruction }</p>
									</div>
								</GridColumn>
								<GridColumn style={ styles.stepControlsContainer }>
									<Form>
										{ renderStepControls() }
										<Button
											color="pink"
											content="Save"
											disabled={ !hasChanged }
											floated="right"
											loading={ saving }
											onClick={ handleSaveEvaluation }
										/>
										{/* next/prev step button */}
									</Form>
								</GridColumn>
							</Grid>
						</Segment>
					</>
					: evalError
						? null
						: <Segment style={ { height: '100px' } }>
							<Dimmer
								active
								inverted
							>
								<Loader inverted />
							</Dimmer>
						</Segment>
				}
			</Sidebar>
		</Portal>
	);
};

export default EvaluationModal;
